/** SASE partners (SASE-35, P35c): a partner's own pages (`/sase/partner…`) and a
 *  customer's page about its partner (`/sase/partner-access…`). Managing a
 *  customer's other SASE pages goes through `lib/actingFor.ts` instead. */
import { http } from "@/lib/http";
import { CUSTOMER_ORG_HEADER } from "@/lib/actingFor";

import type {
  CustomerSummary,
  Paginated,
  Partner,
  PartnerAccess,
  PartnerActivity,
  PartnerBranding,
  PartnerCustomer,
  PartnerInvite,
  PartnerInviteCreated,
  PartnerMember,
  PartnerRole,
} from "./types";

const BASE = "/sase/partner";
const ACCESS = "/sase/partner-access";

// ---- the partner's own pages ------------------------------------------------
export async function getPartner(): Promise<Partner> {
  const { data } = await http.get<Partner>(BASE);
  return data;
}

export async function setBranding(body: PartnerBranding): Promise<Partner> {
  const { data } = await http.put<Partner>(`${BASE}/branding`, body);
  return data;
}

export async function getDashboard(page: number): Promise<Paginated<CustomerSummary>> {
  const { data } = await http.get<Paginated<CustomerSummary>>(`${BASE}/dashboard`, {
    params: { page, page_size: 25 },
  });
  return data;
}

export async function listCustomers(page: number): Promise<Paginated<PartnerCustomer>> {
  const { data } = await http.get<Paginated<PartnerCustomer>>(`${BASE}/customers`, {
    params: { page, page_size: 100 },
  });
  return data;
}

export async function releaseCustomer(orgId: string): Promise<void> {
  await http.delete(`${BASE}/customers/${orgId}`);
}

export async function listMembers(): Promise<Paginated<PartnerMember>> {
  const { data } = await http.get<Paginated<PartnerMember>>(`${BASE}/members`, {
    params: { page_size: 100 },
  });
  return data;
}

export async function addMember(email: string, role: PartnerRole): Promise<PartnerMember> {
  const { data } = await http.post<PartnerMember>(`${BASE}/members`, { email, role });
  return data;
}

export async function updateMember(id: string, role: PartnerRole): Promise<PartnerMember> {
  const { data } = await http.put<PartnerMember>(`${BASE}/members/${id}`, { role });
  return data;
}

export async function removeMember(id: string): Promise<void> {
  await http.delete(`${BASE}/members/${id}`);
}

export async function listInvites(): Promise<Paginated<PartnerInvite>> {
  const { data } = await http.get<Paginated<PartnerInvite>>(`${BASE}/invites`, {
    params: { page_size: 100 },
  });
  return data;
}

export async function createInvite(body: {
  role: PartnerRole;
  note?: string;
  valid_days: number;
}): Promise<PartnerInviteCreated> {
  const { data } = await http.post<PartnerInviteCreated>(`${BASE}/invites`, body);
  return data;
}

export async function deleteInvite(id: string): Promise<void> {
  await http.delete(`${BASE}/invites/${id}`);
}

/** A customer's usage report, made as that customer (P38's machinery, kind
 *  `usage`), downloaded as CSV. */
export async function usageReportCsv(orgId: string): Promise<string> {
  const headers = { [CUSTOMER_ORG_HEADER]: orgId };
  const { data: run } = await http.post<{ id: string; status: string; error: string | null }>(
    "/sase/reports/runs",
    { kind: "usage", window_days: 30 },
    { headers },
  );
  if (run.status !== "ready") throw new Error(run.error ?? "The usage report could not be made.");
  const { data } = await http.get<string>(`/sase/reports/runs/${run.id}/download`, {
    headers,
    responseType: "text",
  });
  return data;
}

// ---- the customer's page about its partner ------------------------------------
export async function getPartnerAccess(): Promise<PartnerAccess> {
  const { data } = await http.get<PartnerAccess>(ACCESS);
  return data;
}

export async function acceptPartner(code: string, role: PartnerRole): Promise<PartnerAccess> {
  const { data } = await http.post<PartnerAccess>(ACCESS, { code, role });
  return data;
}

export async function updatePartnerAccess(role: PartnerRole): Promise<PartnerAccess> {
  const { data } = await http.put<PartnerAccess>(ACCESS, { role });
  return data;
}

export async function revokePartner(): Promise<void> {
  await http.delete(ACCESS);
}

export async function partnerActivity(): Promise<Paginated<PartnerActivity>> {
  const { data } = await http.get<Paginated<PartnerActivity>>(`${ACCESS}/activity`, {
    params: { page_size: 10 },
  });
  return data;
}

// ---- the operator's (Administration → Points of presence → Partners) ----------
export interface PartnerAdmin {
  id: string;
  org_id: string;
  org_name: string;
  name: string;
  status: "active" | "suspended";
  max_customers: number;
  customers: number;
  members: number;
  created_at: string;
}

const ADMIN = "/admin/sase/partners";

export async function adminListPartners(): Promise<Paginated<PartnerAdmin>> {
  const { data } = await http.get<Paginated<PartnerAdmin>>(ADMIN, { params: { page_size: 100 } });
  return data;
}

export async function adminCreatePartner(body: {
  org_id: string;
  admin_email: string;
  name?: string;
  max_customers?: number;
}): Promise<PartnerAdmin> {
  const { data } = await http.post<PartnerAdmin>(ADMIN, body);
  return data;
}

export async function adminUpdatePartner(
  id: string,
  body: { name?: string; status?: "active" | "suspended"; max_customers?: number },
): Promise<PartnerAdmin> {
  const { data } = await http.put<PartnerAdmin>(`${ADMIN}/${id}`, body);
  return data;
}
